import { useCallback, useMemo } from 'react';
import { DaaSNotification } from '../api/contracts/notifications';
import { useNotifications, useReadNotification } from '../api/hooks/notifications';
import { useUser } from '../authentication/hooks/user';

export const useMyNotifications = () => {
  const { role } = useUser();
  const [notifications] = useNotifications(role.partnerId, role.customerId);

  return useMemo(() => {
    return notifications.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
  }, [notifications]);
};

export const useRecentNotifications = () => {
  const notifications = useMyNotifications();

  return useMemo(() => {
    return notifications.slice(0, 10);
  }, [notifications]);
};

export const isNotificationRead = (notification: DaaSNotification) => {
  return !!notification.notificationStatuses.find(s => !!s.processedOn);
};

export const useUnreadNotifications = () => {
  const notifications = useMyNotifications();

  return useMemo(() => {
    return notifications.filter(notification => !isNotificationRead(notification));
  }, [notifications]);
};

const useMarkNotificationsAsRead = () => {
  const unreadNotifications = useUnreadNotifications();
  const { role } = useUser();
  const read = useReadNotification(role.partnerId, role.customerId);

  return useCallback(
    (notificationIds: string[]) => {
      const neededToMark = notificationIds.filter(i => !!unreadNotifications.find(n => n.id === i));
      neededToMark.forEach(notId => read.mutate(notId));
    },
    [unreadNotifications, read]
  );
};

export const useMarkNotifcitionAsRead = () => {
  const mark = useMarkNotificationsAsRead();

  return useCallback((id: string) => mark([id]), [mark]);
};

export const useMarkAllNotificationsAsRead = () => {
  const notifications = useUnreadNotifications();
  const mark = useMarkNotificationsAsRead();

  return useCallback(() => {
    mark(notifications.map(n => n.id));
  }, [notifications, mark]);
};
